import { DashboardDomain } from '../../types'
import { DailyRow, dailyRates, sumMeasure } from '../../math/series'
import { outlierDays } from '../../math/statistics'
import { DateWindow, IsoDate, baselineWindowFor, datesIn } from '../../math/windows'

export type AcquisitionRow = {
    domain: DashboardDomain
    permissionsShown: number
    subscriptions: number
    /** Subscriptions over permissions shown across the range; null when nothing was shown. */
    rate: number | null
    /** The range's rate less the domain's own trailing 28-day rate, in percentage points; null where either is unknown. */
    vsOwnAvgPts: number | null
    /** A day in the range sits further from the domain's own 28 days than their usual spread. */
    outlier: boolean
    outlierDays: IsoDate[]
}

const withoutGaps = (values: Array<number | null>): number[] =>
    values.filter((value): value is number => value !== null)

const rateOver = (rows: DailyRow[], window: DateWindow): number | null => {
    const permissionsShown = sumMeasure(rows, 'permissionsShown', window)
    return permissionsShown === 0 ? null : sumMeasure(rows, 'subscriptions', window) / permissionsShown
}

/**
 * One row per domain in the filter, the busiest first. A rate is recomputed from the summed parts, never
 * averaged from daily rates, and a domain is only ever compared with its own trailing 28 days.
 */
export const buildAcquisitionRows = (args: {
    rows: DailyRow[]
    domains: DashboardDomain[]
    /** Empty means every domain. */
    selectedDomainIds: number[]
    range: DateWindow
}): AcquisitionRow[] => {
    const { rows, domains, selectedDomainIds, range } = args
    const baselineWindow = baselineWindowFor(range)
    const rowsByDomainId = new Map<number, DailyRow[]>()
    for (const row of rows) {
        rowsByDomainId.set(row.domainId, [...(rowsByDomainId.get(row.domainId) ?? []), row])
    }
    const filteredDomains = selectedDomainIds.length
        ? domains.filter((domain) => selectedDomainIds.includes(domain.id))
        : domains

    return filteredDomains
        .map((domain): AcquisitionRow => {
            const domainRows = rowsByDomainId.get(domain.id) ?? []
            const rate = rateOver(domainRows, range)
            const baselineRate = rateOver(domainRows, baselineWindow)
            const rangeDates = datesIn(range)
            const rangeDailyRates = dailyRates(domainRows, 'subscriptions', 'permissionsShown', range)
            const rangeRatesByDate: Record<IsoDate, number> = {}
            rangeDates.forEach((date, index) => {
                const dailyRate = rangeDailyRates[index]
                if (dailyRate !== null) rangeRatesByDate[date] = dailyRate
            })
            const unusualDays = outlierDays(
                rangeRatesByDate,
                withoutGaps(dailyRates(domainRows, 'subscriptions', 'permissionsShown', baselineWindow)),
            )
            return {
                domain,
                permissionsShown: sumMeasure(domainRows, 'permissionsShown', range),
                subscriptions: sumMeasure(domainRows, 'subscriptions', range),
                rate,
                vsOwnAvgPts: rate === null || baselineRate === null ? null : (rate - baselineRate) * 100,
                outlier: unusualDays.length > 0,
                outlierDays: unusualDays,
            }
        })
        .sort(
            (first, second) =>
                second.subscriptions - first.subscriptions ||
                first.domain.displayName.localeCompare(second.domain.displayName),
        )
}

export type AcquisitionTotals = {
    permissionsShown: number
    subscriptions: number
    /** Total subscriptions over total permissions shown, never an average of the rows' rates; null when nothing was shown. */
    rate: number | null
    /** The domains in the filter that showed a permission prompt in the range. */
    domainsShowingPermissionsCount: number
    domainCount: number
}

/**
 * The Total row, over the rows given. The page's domain selector decides which domains those are; the table's
 * search only decides which of them are listed, so it is not applied here.
 */
export const buildAcquisitionTotals = (rows: AcquisitionRow[]): AcquisitionTotals => {
    const permissionsShown = rows.reduce((total, row) => total + row.permissionsShown, 0)
    const subscriptions = rows.reduce((total, row) => total + row.subscriptions, 0)
    return {
        permissionsShown,
        subscriptions,
        rate: permissionsShown === 0 ? null : subscriptions / permissionsShown,
        domainsShowingPermissionsCount: rows.filter((row) => row.permissionsShown > 0).length,
        domainCount: rows.length,
    }
}
